import React, { useCallback, useEffect, useState } from "react";
import Table from "../../components/datatable/datatable";
import SearchComponent from "../../components/search";
import { customerFetchPartnerSubsidieApplications } from "../../core/axios/endpoints";

// Read-only overzicht voor subsidie-partners: aanvragen (EIA / EIA Zon / ISDE)
// die via hun partner-link zijn binnengekomen. Geen PII, geen bedragen, geen
// detailpagina (contract 4 in AGENTS.md 3.28).

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");
// datumOpdrachtBevestiging is een yyyy-mm-dd string.
const fmtIsoDate = (s) => {
  if (!s || typeof s !== "string") return "-";
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}-${m}-${y}` : s;
};

const STATUS_LABELS = {
  "In-behandeling": { label: "In behandeling", color: "bg-amber-100 text-amber-800" },
  "Ingediend-bij-rvo": { label: "Ingediend bij RVO", color: "bg-sky-100 text-sky-800" },
  Goedgekeurd: { label: "Goedgekeurd", color: "bg-emerald-100 text-emerald-800" },
  Afgekeurd: { label: "Afgekeurd", color: "bg-rose-100 text-rose-800" },
};

const INVOICE_STATUS_LABELS = {
  none: { label: "Nog niet van toepassing", color: "bg-slate-100 text-slate-700" },
  pending: { label: "Nog niet gefactureerd", color: "bg-violet-100 text-violet-800" },
  invoiced: { label: "Gefactureerd", color: "bg-sky-100 text-sky-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  credited: { label: "Gecrediteerd", color: "bg-rose-100 text-rose-800" },
};

const Pill = ({ cfg }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.color}`}>
    {cfg.label}
  </span>
);

const PartnerSubsidieAanvragenPage = () => {
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [regling, setRegling] = useState("");

  const fetchData = useCallback(
    async ({ page, pageSize, resetPageSize }, search = searchText, reg = regling) => {
      setLoading(true);
      setError(null);
      try {
        const body = await customerFetchPartnerSubsidieApplications({
          page,
          limit: pageSize,
          search,
          regling: reg,
        });
        const payload = body?.data ?? body;
        setData({
          data: (payload?.docs ?? []).map((item, index) => ({ ...item, id: index })),
          totalDataCount: payload?.totalDocs ?? payload?.pagination?.totalDocs ?? 0,
          resetPageSize,
        });
      } catch (e) {
        console.error("Partner subsidie-lijst ophalen mislukt:", e);
        setError(
          e?.response?.data?.error ??
            "De aanvragen konden niet worden geladen. Probeer het later opnieuw.",
        );
      } finally {
        setLoading(false);
      }
    },
    [searchText, regling],
  );

  useEffect(() => {
    fetchData({ page: 1, pageSize: 25, resetPageSize: false });
  }, []);

  const handleSearch = (q) => {
    setSearchText(q);
    fetchData({ page: 1, pageSize: 25, resetPageSize: true }, q, regling);
  };

  const handleRegling = (value) => {
    setRegling(value);
    fetchData({ page: 1, pageSize: 25, resetPageSize: true }, searchText, value);
  };

  const columns = [
    {
      title: "Kenmerk",
      dataIndex: "Kenmerk",
      render: (v) => <span className="font-mono text-sm">{v || "-"}</span>,
    },
    {
      title: "Handelsnaam",
      dataIndex: "handelsNaam",
      render: (v) => <span className="font-medium text-gray-900">{v || "-"}</span>,
      sorter: (a, b) => (a.handelsNaam ?? "").localeCompare(b.handelsNaam ?? ""),
    },
    {
      title: "KvK",
      dataIndex: "KvkNummer",
      render: (v) => <span className="text-sm text-slate-600">{v || "-"}</span>,
    },
    {
      title: "Plaats",
      dataIndex: "plaats",
      render: (v) => <span className="text-sm text-slate-600">{v || "-"}</span>,
    },
    {
      title: "Regeling",
      dataIndex: "regling",
      render: (v) => <span className="text-sm">{v || "-"}</span>,
    },
    {
      title: "Datum opdracht",
      dataIndex: "datumOpdrachtBevestiging",
      render: (v) => <span className="text-sm">{fmtIsoDate(v)}</span>,
    },
    {
      title: "Ingediend",
      dataIndex: "createdAt",
      render: (v) => <span className="text-sm">{fmtDate(v)}</span>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (
        <Pill cfg={STATUS_LABELS[v] ?? { label: v || "-", color: "bg-gray-100 text-gray-700" }} />
      ),
    },
    {
      title: "Facturatie",
      dataIndex: "partnerInvoiceStatus",
      render: (v) => <Pill cfg={INVOICE_STATUS_LABELS[v ?? "none"] ?? INVOICE_STATUS_LABELS.none} />,
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="p-4">
          <div className="mb-4">
            <h1 className="font-display text-2xl text-gray-900">Subsidie-aanvragen</h1>
            <p className="mt-1 text-sm text-slate-600">
              Overzicht van alle subsidie-aanvragen (EIA, EIA Zon, ISDE) die via uw partner-link zijn binnengekomen.
            </p>
          </div>
          <SearchComponent onSearch={handleSearch} type={"partner-subsidie"} />

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={regling}
              onChange={(e) => handleRegling(e.target.value)}
              className="rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
            >
              <option value="">Alle regelingen</option>
              <option value="EIA">EIA</option>
              <option value="EIA Zon">EIA Zon</option>
              <option value="ISDE">ISDE</option>
            </select>
            <span className="ml-auto text-xs text-gray-500">
              {data?.totalDataCount ?? 0} aanvragen
            </span>
          </div>

          {error ? (
            <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
              {error}
            </div>
          ) : null}
        </div>
        <div className="table-responsive">
          <Table columns={columns} response={data} fetchData={fetchData} loading={loading} />
        </div>
      </div>
    </div>
  );
};

export default PartnerSubsidieAanvragenPage;
